"use client";

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-8xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Image + badge */}
          <div className="reveal-on-scroll relative">
            <div className="relative rounded-3xl overflow-hidden shadow-card-hover" style={{ aspectRatio: '4/3' }}>
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1cb71eecb-1772890149290.png"
                alt="Professional medical equipment and healthcare devices in a clinical setting"
                fill
                className="object-cover"
                priority />
              
              <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(30,95,142,0.15) 0%, transparent 60%)' }} />
            </div>

            {/* Floating stat badge */}
            <div className="absolute -bottom-6 -right-4 lg:right-8 bg-white rounded-2xl shadow-card-hover p-5 border border-border">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1E5F8E, #2472A4)' }}>
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <div className="font-display font-800 text-foreground" style={{ fontWeight: 800, fontSize: '22px' }}>專業認證</div>
                  <div className="text-muted text-xs" style={{ fontWeight: 500 }}>Certified Medical Devices</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Text */}
          <div className="space-y-8">
            <div className="reveal-on-scroll">
              <span className="section-label">關於我們 · About Us</span>
            </div>

            <div className="reveal-on-scroll reveal-delay-1">
              <h2 className="font-display font-800 text-foreground leading-tight" style={{ fontWeight: 800, fontSize: 'clamp(32px, 4vw, 52px)' }}>
                香港醫療器材
                <br />
                <span style={{ color: 'var(--color-primary)' }}>專業行銷服務</span>
              </h2>
            </div>

            <div className="reveal-on-scroll reveal-delay-2 space-y-4 text-muted leading-relaxed" style={{ fontSize: '16px' }}>
              <p>
                醫時醫療有限公司（Easy Medical Limited）是一家專注於醫療器材行銷及服務的香港企業，總部位於荃灣區青山公路 483A 號。
              </p>
              <p>
                我們深耕三大核心業務領域：<strong className="text-foreground">生命資訊與支援</strong>、<strong className="text-foreground">體外診斷</strong>及<strong className="text-foreground">醫學影像</strong>，為醫院、診所及各醫療機構提供高品質、可靠的醫療設備及配套試劑。
              </p>
              <p>
                憑藉豐富的行業經驗與專業的技術團隊，我們致力於為客戶提供最優質的售前諮詢、設備安裝及售後維護服務。
              </p>
            </div>

            {/* Stats row */}
            <div className="reveal-on-scroll reveal-delay-3 grid grid-cols-3 gap-6 pt-4 border-t border-border">
              {[
              { num: '3', label: '業務領域', sub: 'Core Divisions' },
              { num: '全港', label: '服務覆蓋', sub: 'HK Coverage' },
              { num: '24/7', label: '技術支援', sub: 'Tech Support' }]?.
              map((s) =>
              <div key={s?.label} className="text-center">
                  <div className="font-display font-800 text-primary" style={{ fontWeight: 800, fontSize: '28px' }}>{s?.num}</div>
                  <div className="font-display font-700 text-foreground text-sm" style={{ fontWeight: 700 }}>{s?.label}</div>
                  <div className="text-muted text-xs" style={{ fontWeight: 400 }}>{s?.sub}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>);

}